// src/shared/components/layout/Footer.jsx
import React from 'react';
import { Box, Stack, Typography, Link, IconButton, Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';

const logoSrc = '/images/logo2.png';

const Footer = (props) => {
  const { sx: sxProp, ...otherProps } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const year = new Date().getFullYear();

  // ── Mobile footer: Anthropic-style two-row layout ─────────────────────
  if (isMobile) {
    const socials = [
      { icon: <XIcon fontSize="small" />, href: 'https://x.com/', label: 'X' },
      { icon: <LinkedInIcon fontSize="small" />, href: 'https://www.linkedin.com/', label: 'LinkedIn' },
      { icon: <YouTubeIcon fontSize="small" />, href: 'https://youtube.com/', label: 'YouTube' },
      { icon: <InstagramIcon fontSize="small" />, href: 'https://instagram.com/', label: 'Instagram' },
    ];

    return (
      <Box
        component="footer"
        {...otherProps}
        sx={{
          px: 2.5,
          py: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          ...sxProp,
        }}
      >
        {/* Top — logo above brand name */}
        <Stack spacing={0.75} alignItems="center" sx={{ mb: 2.5 }}>
          <Box
            component="img"
            src={logoSrc}
            alt="MSI Lodging"
            sx={{ height: 34, width: 'auto' }}
          />
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 2,
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: '0.8rem',
            }}
          >
            BY MSI LODGING
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.72rem',
              textAlign: 'center',
            }}
          >
            © {year} MSI LODGING · Powered by{' '}
            <Link
              href="https://squareplanit.com/"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: '#00aeef' }}
            >
              SquarePlanIT
            </Link>
          </Typography>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Bottom — socials (left) + Contact Us link (right) */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            {socials.map((s) => (
              <IconButton
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': { color: theme.palette.text.primary },
                }}
              >
                {s.icon}
              </IconButton>
            ))}
          </Stack>

          <Link
            href="https://www.msilodging.com/"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.8125rem',
              fontWeight: 600,
              '&:hover': { color: theme.palette.text.primary },
            }}
          >
            Contact Us
          </Link>
        </Stack>
      </Box>
    );
  }

  // ── Desktop footer: original layout ──────────────────────────────────
  return (
    <Box
      {...otherProps}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        ...sxProp,
      }}
    >
      <Typography
        component="a"
        href="https://www.msilodging.com/"
        target="_blank"
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        Contact Us
      </Typography>

      <Typography
        variant="body2"
        sx={{ textAlign: 'center', flexGrow: 1, color: theme.palette.text.secondary }}
      >
        © {year} MSI Lodging. All Rights Reserved
        <br />
        Powered by{' '}
        <Link href="https://squareplanit.com/" color="#00aeef" underline="hover" target="_blank">
          SquarePlanIT
        </Link>
        , your trusted technology partner.
      </Typography>

      <Box component="img" src={logoSrc} alt="Logo" sx={{ height: 30, width: 'auto' }} />
    </Box>
  );
};

export default Footer;